import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import {
  type AppNotification,
  flattenNotifications,
  notificationTarget,
  notificationText,
  type NotificationTranslate,
  useMarkAllRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
} from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import { formatDateTime } from '@/shared/lib/datetime';
import { Avatar, Button, ErrorState, IconButton, Icons, LoadingState, Popover } from '@/shared/ui';

import styles from './NotificationBell.module.css';

/** The signed-in header's notification control: unread count + a popover with the latest items. */
export function NotificationBell() {
  const { t } = useTranslation();
  const unread = useUnreadCount();
  const count = unread.data ?? 0;
  const label = count > 0 ? t('notifications.openUnread', { count }) : t('notifications.open');
  return (
    <Popover
      label={t('notifications.title')}
      width={400}
      alignEnd
      trigger={(props) => (
        <IconButton
          label={label}
          icon={<Icons.bell />}
          badge={count > 0 ? (count > 99 ? '99+' : count) : undefined}
          data-testid="notifications-button"
          className={styles.bell}
          {...props}
        />
      )}
    >
      {(close) => <NotificationList onNavigate={close} />}
    </Popover>
  );
}

function NotificationList({ onNavigate }: { onNavigate: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const viewer = useViewer();
  const zone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const query = useNotifications({ unreadOnly: true });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllRead();
  const items = flattenNotifications(query.data);
  const unread = query.data?.pages[0]?.unread_count ?? 0;

  const open = (n: AppNotification) => {
    if (n.read_at === null) markRead.mutate(n.id);
    const target = notificationTarget(n);
    onNavigate();
    if (target) void navigate(target);
  };

  return (
    <div className={styles.panel} data-testid="notifications-panel">
      <div className={styles.head}>
        <h2 className={styles.title}>{t('notifications.title')}</h2>
        <Button
          size="sm"
          variant="ghost"
          disabled={unread === 0 || markAll.isPending}
          onClick={() => {
            markAll.mutate();
          }}
        >
          {t('notifications.markAll')}
        </Button>
      </div>
      {query.isPending && <LoadingState />}
      {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
      {query.isSuccess && items.length === 0 && (
        <p className={styles.empty}>{t('notifications.empty')}</p>
      )}
      <ul className={styles.list} aria-live="polite">
        {items.map((n) => {
          const { title, detail } = notificationText(t as unknown as NotificationTranslate, n);
          const read = n.read_at !== null;
          return (
            <li key={n.id}>
              <button
                type="button"
                className={`${styles.item} ${read ? '' : styles.unread}`}
                data-read={read ? 'true' : 'false'}
                onClick={() => {
                  open(n);
                }}
              >
                {n.actor ? (
                  <Avatar id={n.actor.user_id} name={n.actor.display_name} size="sm" />
                ) : (
                  <span className={styles.systemIcon} aria-hidden>
                    <Icons.bell />
                  </span>
                )}
                <span className={styles.body}>
                  <span className={styles.text}>{title}</span>
                  {detail && <span className={styles.detail}>{detail}</span>}
                  <span className={styles.when}>
                    {formatDateTime(n.created_at, i18n.language, zone)}
                  </span>
                </span>
                {!read && <span className={styles.dot} aria-label={t('notifications.unread')} />}
              </button>
            </li>
          );
        })}
      </ul>
      <div className={styles.footer}>
        <Link
          to="/app/notifications"
          className={styles.allLink}
          onClick={onNavigate}
          data-testid="all-notifications"
        >
          <Icons.history />
          <span>{t('notifications.all')}</span>
          <Icons.arrowRight />
        </Link>
      </div>
      {query.hasNextPage && (
        <div className={styles.more}>
          <Button
            size="sm"
            disabled={query.isFetchingNextPage}
            onClick={() => {
              void query.fetchNextPage();
            }}
          >
            {t('notifications.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
